import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  profileFetched,
  fetchingProfile,
} from 'containers/Inbox/actions/Session.actions';
import { signInSuccess } from 'legacy/modules/auth/auth.Dux';
import Tracker from 'utils/Tracking';
import classnames from 'classnames';
import useLazyQuery from 'utils/useLazyQuery';
import Api from 'api/index';
import { useForm } from 'react-hook-form';
import { useRef } from 'react';
import usePaste from 'utils/usePaste';
import { useTranslation } from 'react-i18next';
import GoogleTagManager from 'utils/GoogleTagManager';
import { get as getAcquisition } from 'containers/Login/utils/useSourceTracker';

const CODES_LENGTH = 6;

const VerificationCodeForm = ({ prefix, phone, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [ApiCheckVerificationCode, isChecking] = useLazyQuery(
    Api.checkVerificationCode
  );
  const [error, setError] = useState();
  const { register, trigger, handleSubmit } = useForm({
    mode: 'onChange',
  });
  const codesRef = useRef([]);

  const resetError = () => setError();

  const focusNext = (index) =>
    index < CODES_LENGTH - 1 && codesRef.current[index + 1].focus();

  const focusPrev = (index) => index > 0 && codesRef.current[index - 1].focus();

  const getCode = () => codesRef.current.map(({ value }) => value).join('');

  const login = async () => {
    resetError();

    Tracker.event('login button click');

    const code = getCode();
    const sessionId = localStorage.getItem('sessionId');

    try {
      const acquisition = getAcquisition();
      const {
        data: {
          data: { access_token: token, professional: profile },
        },
      } = await ApiCheckVerificationCode({
        prefix,
        phone,
        code,
        sessionId,
        source: acquisition?.source,
        campaign: acquisition?.campaign,
      });

      localStorage.setItem('jwtToken', token);

      const professionalStatus = profile.status;

      if (professionalStatus !== 'new') {
        await getProfile({ token, profile });
      }

      dispatch(signInSuccess({ token, profile }));

      Tracker.setUser({ userHash: profile.hash, professionalStatus });
      Tracker.event('login');

      GoogleTagManager.conversion('bYrmCJv2078CEPKinbUC');
      GoogleTagManager.conversion('AsCsCKnHu9kDENf-wuAo');
    } catch (error) {
      setError(error.response.data.message);
    }
  };

  const getProfile = async ({ token, profile }) => {
    dispatch(fetchingProfile());

    const {
      data: { data },
    } = await Api.getProfile();

    dispatch(profileFetched(data, { token, profile }));
  };

  const handleChange = (event, index) => {
    event.target.value !== '' && focusNext(index);

    trigger().then((isFilled) => isFilled && login());
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !event.target.value) {
      focusPrev(index);
    }
  };

  usePaste({
    onPaste: (paste) => {
      [...paste]
        .slice(0, CODES_LENGTH)
        .forEach((code, index) => (codesRef.current[index].value = code));

      trigger().then((isFilled) => isFilled && login());
    },
  });

  return (
    <>
      <form
        className="relative flex justify-between w-full gap-2 mt-10 mb-16 sm:gap-6"
        onSubmit={handleSubmit(login)}
      >
        {Array(CODES_LENGTH)
          .fill()
          .map((_, index) => (
            <input
              key={index}
              name={`code-${index}`}
              autoComplete="off"
              ref={(element) => {
                register(element, { required: true });
                codesRef.current[index] = element;
              }}
              type="number"
              maxLength="1"
              autoFocus={index === 0}
              disabled={isChecking}
              onChange={(event) => handleChange(event, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={`${classnames({
                error,
                'border border-negative': error,
                'border-none': !error,
              })} bg-background text-dark rounded-lg w-full h-20 text-3xl text-center`}
            />
          ))}
        {error && (
          <div
            data-testid="error-message"
            className="absolute bottom-0 right-0 w-full pt-2 text-sm transform translate-y-full text-negative"
          >
            {t('login__check_code_form_error')}
          </div>
        )}
        {isChecking && (
          <div className="absolute bottom-0 right-0 w-full pt-2 text-sm transform translate-y-full text-primary">
            {t('login__check_code_form_verifying')}
          </div>
        )}
      </form>
      <div className="flex flex-wrap justify-center gap-2 text-sm text-center">
        <span className="text-gray-medium">
          {t('login__check_code_form_retry')}
        </span>
        <button onClick={onClose} className="text-primary">
          {t('login__check_code_form_retry_link')}
        </button>
      </div>
    </>
  );
};

export default VerificationCodeForm;
